import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'APP/Hooks/redux'
import styled from 'styled-components/native'
import { SafeAreaView } from 'react-native-safe-area-context'

import { Images } from 'APP/Themes'
import I18n from 'APP/I18n'
import LoginActions from 'APP/Redux/LoginRedux'
import usePrevious from 'APP/Hooks/usePrevious'

import Modal from 'APP/Components/Modal'
import LogoHeader from 'APP/Components/LogoHeader'
import Button from 'APP/Converse/Button'
import Typography from 'APP/Converse/Typography'
import { ResponsiveView } from 'APP/Converse/Layout'

const ScreenContainer = styled(SafeAreaView).attrs({
  edges: ['bottom', 'left', 'right'],
})`
  flex: 1;
`

const ContentContainer = styled(ResponsiveView)`
  gap: ${({ theme }) => theme.metrics.baseMargin * 2}px;
  padding: ${({ theme }) => theme.metrics.baseMargin}px;
  justify-content: space-between;
  flex: 1;
`

const InnerContentContainer = styled(ResponsiveView)`
  gap: ${({ theme }) => theme.metrics.baseMargin * 2}px;
`

const ButtonContainer = styled.View`
  gap: ${({ theme }) => theme.metrics.baseMargin}px;
`

const VerifyEmailHelpScreen = () => {
  const dispatch = useDispatch()
  const [showEmailSentModal, setShowEmailSentModal] = useState(false)
  const [showGenericErrorModal, setShowGenericErrorModal] = useState(false)
  const { email } = useSelector((state) => state.login?.customClaims)
  const { emailVerificationResendRunning, emailVerificationResendFailed } = useSelector(
    (state) => state.login
  )
  const previousEmailVerificationResendRunning = usePrevious(emailVerificationResendRunning)

  useEffect(() => {
    if (previousEmailVerificationResendRunning && !emailVerificationResendRunning) {
      if (emailVerificationResendFailed) {
        setShowGenericErrorModal(true)
      } else {
        setShowEmailSentModal(true)
      }
    }
  }, [
    previousEmailVerificationResendRunning,
    emailVerificationResendRunning,
    emailVerificationResendFailed,
  ])

  const renderEmailSentModalBody = () => {
    return (
      <Typography variant="bodyNormal">
        {I18n.t('VerifyEmailScreen.emailSentModal.body')}
        <Typography variant="bodyNormalBold"> {email}.</Typography>
      </Typography>
    )
  }

  return (
    <ScreenContainer>
      <ContentContainer>
        <InnerContentContainer>
          <LogoHeader
            source={Images.logoBlue}
            title={I18n.t('VerifyEmailHelpScreen.title')}
            subtitle={I18n.t('VerifyEmailHelpScreen.subtitle')}
          />

          <ButtonContainer>
            <Button
              variant="primary"
              widthVariant="full"
              onPress={() => dispatch(LoginActions.emailVerificationResendRequest())}
              title={I18n.t('VerifyEmailHelpScreen.resendEmailVerification')}
              testID="EMAIL_VERIFICATION_RESEND_BTN"
            />

            <Button
              variant="secondary"
              widthVariant="full"
              onPress={() =>
                dispatch(LoginActions.logout('Log out from verify email screen', true))
              }
              title={I18n.t('AccountScreen.signOut')}
              textColor="error"
              lineColor="error"
              testID="LOGOUT_BTN"
            />
          </ButtonContainer>
        </InnerContentContainer>
      </ContentContainer>

      <Modal
        statusBarTranslucent
        visible={showEmailSentModal}
        transparent
        title={I18n.t('VerifyEmailScreen.emailSentModal.title')}
        subtitle={renderEmailSentModalBody()}
        primaryActionText={I18n.t('VerifyEmailScreen.emailSentModal.action')}
        handleCloseModal={() => setShowEmailSentModal(false)}
        handlePrimaryAction={() => setShowEmailSentModal(false)}
        testID="EMAIL_SENT_MODAL"
      />
      <Modal
        statusBarTranslucent
        visible={showGenericErrorModal}
        transparent
        title={I18n.t('VerifyEmailScreen.error.generic.title')}
        subtitle={I18n.t('VerifyEmailScreen.error.generic.body')}
        primaryActionText={I18n.t('VerifyEmailScreen.error.generic.action')}
        handleCloseModal={() => setShowGenericErrorModal(false)}
        handlePrimaryAction={() => setShowGenericErrorModal(false)}
        testID="GENERIC_ERROR_MODAL"
      />
    </ScreenContainer>
  )
}

export default VerifyEmailHelpScreen
